import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

// Local images
import aiImage from "../../../assets/ai image anal.jpg";
import imageupload from "../../../assets/plate scanning.jpg";
import personalrem from "../../../assets/Personalized Tracking.jpg";
import nute from "../../../assets/Nutritional Info Visualization.jpg";

// Steps
const steps = [
  {
    title: "1. Image Upload",
    text:
      "Upload your meal photo using our guided smart camera that ensures clarity and perfect lighting for better AI detection. You can even use live preview before snapping.",
    img: imageupload,
  },
  {
    title: "2. AI Analysis",
    text:
      "Our powerful AI scans your meal image to detect food types, ingredients, and portion sizes with high accuracy. It adapts to different cuisines and plate compositions.",
    img: aiImage,
  },
  {
    title: "3. Nutritional Info",
    text:
      "Get detailed nutritional information including calories, macros, micros, allergens, and more. Stay aware of your nutrient intake without manual calculations.",
    img: nute,
  },
  {
    title: "4. Personalized Tracking",
    text:
      "Automatically log meals, monitor your daily goals, and receive intelligent tips based on your activity, preferences, and history. It’s like having a nutritionist in your pocket!",
    img: personalrem,
  },
];

const stepVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const fillVariants = {
  hidden: { height: "0%", opacity: 0 },
  visible: {
    height: "100%",
    opacity: 0.25,
    transition: { duration: 1.5, ease: "easeOut" },
  },
};

const SkeletonStep = () => (
  <div className="grid md:grid-cols-2 gap-6 mb-16 animate-pulse">
    <div className="space-y-4">
      <div className="h-6 w-2/3 bg-gray-700 rounded"></div>
      <div className="h-4 w-full bg-gray-700 rounded"></div>
      <div className="h-4 w-5/6 bg-gray-700 rounded"></div>
      <div className="h-4 w-4/5 bg-gray-700 rounded"></div>
    </div>
    <div className="w-full h-[400px] bg-gray-700 rounded-xl"></div>
  </div>
);

export default function FeatureAiFoodDetection() {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!localStorage.getItem("token")) {
      navigate("/login");
    } else if (file) {
      const formData = new FormData();
      formData.append("image", file);

      fetch("https://your-api-url.com/upload", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formData,
      })
        .then((res) => res.json())
        .then((data) => {
          console.log("Upload success:", data);
        })
        .catch((err) => {
          console.error("Upload failed:", err);
        });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#080f08] via-[#132118] to-[#0a1a11] text-white px-6 py-16 lg:px-32 relative overflow-hidden font-sans">
      <div className="absolute bottom-0 left-0 w-full h-48 overflow-hidden pointer-events-none">
        <motion.svg
          viewBox="0 0 1440 150"
          className="w-full h-full"
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        >
          <path fill="#0e1a10" d="M0,30 C360,120 1080,0 1440,90 L1440,150 L0,150 Z" />
        </motion.svg>
      </div>

      <motion.h1
        className="text-4xl md:text-5xl font-extrabold text-green-400 mb-12 text-center tracking-tight z-10 relative"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        How Our Smart Food Detection Works
      </motion.h1>

      {loading
        ? Array(4).fill().map((_, i) => <SkeletonStep key={i} />)
        : steps.map((step, idx) => (
            <motion.div
              key={idx}
              className={`mb-24 flex flex-col md:grid md:grid-cols-2 items-center gap-10 z-10 relative ${idx % 2 === 1 ? "md:flex-row-reverse" : ""}`}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={stepVariants}
              transition={{ delay: idx * 0.2 }}
            >
              <div className="space-y-5">
                <h2 className="text-2xl md:text-3xl font-bold text-green-300 drop-shadow-md">
                  {step.title}
                </h2>
                <p className="text-gray-300 text-base md:text-lg leading-relaxed">
                  {step.text}
                </p>
              </div>

              <motion.div
                className="relative w-full max-w-[420px] mx-auto aspect-[3/4] overflow-hidden rounded-3xl shadow-2xl border border-green-400 hover:shadow-green-400/40"
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <motion.img
                  src={step.img}
                  alt={step.title}
                  className="absolute inset-0 w-full h-full object-cover object-center rounded-3xl"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                  className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-green-400 to-transparent z-10"
                  variants={fillVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                />
              </motion.div>
            </motion.div>
          ))}

      <div className="flex justify-center mt-16">
        <label className="bg-gradient-to-r from-green-400 to-green-600 hover:from-green-500 hover:to-green-700 text-white px-8 py-4 rounded-full cursor-pointer shadow-lg transform transition hover:scale-105">
          Upload Your Meal Image
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
          />
        </label>
      </div>

      <motion.p
        className="text-center text-gray-400 mt-24 z-10 relative text-sm md:text-base max-w-3xl mx-auto"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 1 }}
      >
        At <span className="text-green-400 font-semibold">FitTrack</span>, we blend AI with nutrition science to create a personalized, powerful wellness experience. Whether you're tracking calories or optimizing macros, we're here to guide you step-by-step.
      </motion.p>
    </div>
  );
}
